import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import EmptyState from '../components/EmptyState';
import InlineAlert from '../components/InlineAlert';
import PageHeader from '../components/PageHeader';
import { fetchSalesforceAccountById, fetchSalesforceAccounts } from '../services/salesforceService';
import { exportAccountsAsCsv, exportAccountsAsExcel, getAccountExportFileBaseName } from '../utils/accountExport';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SORT = { column: 'createdDate', direction: 'desc' };

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const formatDateTime = (value) => {
  if (!value) return 'Unavailable';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unavailable';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(parsed);
};

const normalizeAbsoluteHttpUrl = (value) => {
  if (typeof value !== 'string') return null;
  const trimmedValue = value.trim();
  return /^https?:\/\//i.test(trimmedValue) ? trimmedValue : null;
};

const getSalesforceAccountUrl = (account) => {
  return normalizeAbsoluteHttpUrl(account?.sfdcAccountUrl)
    || normalizeAbsoluteHttpUrl(account?.sfdcUrl)
    || normalizeAbsoluteHttpUrl(account?.salesforceUrl);
};

const formatAddress = (account) => {
  if (account?.fullBillingAddress) {
    return account.fullBillingAddress;
  }

  return [
    account?.billingStreet,
    account?.billingCity,
    account?.billingState,
    account?.billingPostalCode,
    account?.billingCountry
  ].filter(Boolean).join(', ') || 'No billing address on file';
};

const formatRelativeTime = (value) => {
  if (!value) return 'Unavailable';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unavailable';

  const diffMs = Date.now() - parsed.getTime();
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;
  const weekMs = 7 * dayMs;

  if (diffMs < minuteMs) return 'Just now';
  if (diffMs < hourMs) return `${Math.floor(diffMs / minuteMs)}m ago`;
  if (diffMs < dayMs) return `${Math.floor(diffMs / hourMs)}h ago`;
  if (diffMs < weekMs) return `${Math.floor(diffMs / dayMs)}d ago`;
  if (diffMs < 30 * dayMs) return `${Math.floor(diffMs / weekMs)}w ago`;

  return formatDateTime(value);
};

const getTypeBadgeClassName = (type) => {
  if (!type) return 'accounts-page__badge accounts-page__badge--default';

  const normalizedType = type.toLowerCase();

  if (normalizedType.includes('customer')) return 'accounts-page__badge accounts-page__badge--customer';
  if (normalizedType.includes('partner')) return 'accounts-page__badge accounts-page__badge--partner';
  if (normalizedType.includes('prospect')) return 'accounts-page__badge accounts-page__badge--prospect';
  if (normalizedType.includes('competitor')) return 'accounts-page__badge accounts-page__badge--competitor';
  if (normalizedType.includes('investor')) return 'accounts-page__badge accounts-page__badge--investor';

  return 'accounts-page__badge accounts-page__badge--default';
};

const getLocationString = (account) => {
  const locationParts = [account?.billingCity, account?.billingState].filter(Boolean);
  if (locationParts.length > 0) return locationParts.join(', ');
  if (account?.billingCountry) return account.billingCountry;
  return 'No location';
};

const hasMissingContactData = (account) => {
  return !account?.phone || !account?.billingStreet || !account?.billingCity || !account?.billingState || !account?.billingPostalCode;
};

const wasCreatedInLast30Days = (value) => {
  if (!value) return false;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return false;

  const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
  return parsed.getTime() >= thirtyDaysAgo;
};

const buildPageNumbers = (currentPage, totalPages) => {
  if (totalPages <= 1) return [1];

  const pages = [];
  const startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(totalPages, currentPage + 2);

  if (startPage > 1) {
    pages.push(1);
    if (startPage > 2) pages.push('ellipsis-start');
  }

  for (let pageNumber = startPage; pageNumber <= endPage; pageNumber += 1) {
    pages.push(pageNumber);
  }

  if (endPage < totalPages) {
    if (endPage < totalPages - 1) pages.push('ellipsis-end');
    pages.push(totalPages);
  }

  return pages;
};

const getAccountSortValue = (account, column) => {
  switch (column) {
    case 'name':
      return account?.name || '';
    case 'accountNumber':
      return account?.accountNumber || '';
    case 'id':
      return account?.id || '';
    case 'industry':
      return account?.industry || '';
    case 'type':
      return account?.type || '';
    case 'phone':
      return account?.phone || '';
    case 'location':
      return getLocationString(account);
    case 'createdDate': {
      const parsed = Date.parse(account?.createdDate || '');
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    default:
      return '';
  }
};

const compareSortValues = (leftValue, rightValue) => {
  if (typeof leftValue === 'number' && typeof rightValue === 'number') {
    return leftValue - rightValue;
  }

  return String(leftValue).localeCompare(String(rightValue), undefined, {
    numeric: true,
    sensitivity: 'base'
  });
};

const toAutomationKey = (value) => String(value || 'unavailable')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '') || 'unavailable';

const getAccountAutomationKey = (account, index) => {
  return toAutomationKey(account?.id || account?.accountNumber || account?.name || `row-${index + 1}`);
};

const Accounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const deferredSearchQuery = useDeferredValue(searchQuery);

  const loadAccounts = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const result = await fetchSalesforceAccounts({
        limit: 2000,
        offset: 0,
        industry: 'Utilities'
      });

      setAccounts(result.accounts);
      setLastUpdated(new Date());
      setCurrentPage(1);
      setSortConfig(DEFAULT_SORT);
    } catch (requestError) {
      setError(requestError?.message || 'Unable to load Salesforce accounts.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const metrics = useMemo(() => {
    const newInLast30Days = accounts.filter((account) => wasCreatedInLast30Days(account?.createdDate)).length;
    const missingContactData = accounts.filter((account) => hasMissingContactData(account)).length;
    const recentServiceRequests = 0;

    return {
      newInLast30Days,
      missingContactData,
      recentServiceRequests
    };
  }, [accounts]);

  const filteredAccounts = useMemo(() => {
    const normalizedQuery = deferredSearchQuery.trim().toLowerCase();

    if (!normalizedQuery) {
      return accounts;
    }

    return accounts.filter((account) => {
      return [
        account?.name,
        account?.accountNumber,
        account?.industry,
        account?.type,
        account?.phone,
        account?.website,
        account?.billingCity,
        account?.billingState,
        account?.billingCountry,
        account?.fullBillingAddress,
        account?.id
      ].some((value) => typeof value === 'string' && value.toLowerCase().includes(normalizedQuery));
    });
  }, [accounts, deferredSearchQuery]);

  const sortedAccounts = useMemo(() => {
    return [...filteredAccounts].sort((left, right) => {
      const leftValue = getAccountSortValue(left, sortConfig.column);
      const rightValue = getAccountSortValue(right, sortConfig.column);
      const comparison = compareSortValues(leftValue, rightValue);

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [filteredAccounts, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedAccounts.length / pageSize));

  const pagedAccounts = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedAccounts.slice(startIndex, startIndex + pageSize);
  }, [currentPage, sortedAccounts, pageSize]);

  const pageInfo = useMemo(() => {
    if (sortedAccounts.length === 0) {
      return 'Showing 0 of 0 accounts';
    }

    const startIndex = (currentPage - 1) * pageSize + 1;
    const endIndex = Math.min(currentPage * pageSize, sortedAccounts.length);
    return `Showing ${startIndex} to ${endIndex} of ${sortedAccounts.length} accounts`;
  }, [currentPage, sortedAccounts.length, pageSize]);

  const pageNumbers = useMemo(() => buildPageNumbers(currentPage, totalPages), [currentPage, totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [deferredSearchQuery, pageSize, sortConfig]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSort = useCallback((column) => {
    setSortConfig((currentSort) => {
      if (currentSort.column === column) {
        return {
          column,
          direction: currentSort.direction === 'asc' ? 'desc' : 'asc'
        };
      }

      return {
        column,
        direction: column === 'createdDate' ? 'desc' : 'asc'
      };
    });
  }, []);

  const getSortIcon = useCallback((column) => {
    if (sortConfig.column !== column) return 'bi-arrow-down-up';
    return sortConfig.direction === 'asc' ? 'bi-sort-up' : 'bi-sort-down';
  }, [sortConfig]);

  const getAriaSort = useCallback((column) => {
    if (sortConfig.column !== column) return 'none';
    return sortConfig.direction === 'asc' ? 'ascending' : 'descending';
  }, [sortConfig]);

  const loadDetailedAccountsForExport = useCallback(async (accountsToExport) => {
    const batchSize = 10;
    const exportResults = [];

    for (let index = 0; index < accountsToExport.length; index += batchSize) {
      const batch = accountsToExport.slice(index, index + batchSize);
      const detailedBatch = await Promise.all(batch.map(async (account) => {
        if (!account?.id) {
          return account;
        }

        const result = await fetchSalesforceAccountById(account.id);
        return result.account;
      }));

      exportResults.push(...detailedBatch);
    }

    return exportResults;
  }, []);

  const handleExport = useCallback(async (format) => {
    if (sortedAccounts.length === 0 || isExporting) {
      return;
    }

    setIsExporting(true);
    setExportError('');

    try {
      const detailedAccounts = await loadDetailedAccountsForExport(sortedAccounts);
      const fileBaseName = getAccountExportFileBaseName();

      if (format === 'csv') {
        exportAccountsAsCsv(detailedAccounts, fileBaseName);
      } else {
        await exportAccountsAsExcel(detailedAccounts, fileBaseName);
      }
    } catch (requestError) {
      setExportError(requestError?.message || 'Unable to export account data.');
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, loadDetailedAccountsForExport, sortedAccounts]);

  if (isLoading) {
    return (
      <section className="accounts-page__loading" aria-busy="true" aria-live="polite" data-testid="accounts-loading">
        <div className="accounts-page__loading-overlay">
          <div className="accounts-page__loading-card text-center shadow-lg">
            <div className="accounts-page__loading-inner">
              <div className="accounts-page__loading-mark" aria-hidden="true">
                <i className="bi bi-lightning-charge-fill"></i>
              </div>
              <div className="spinner-border text-tse-primary accounts-page__loading-spinner" role="status">
                <span className="visually-hidden">Loading</span>
              </div>
              <h1 className="accounts-page__loading-title">Loading Salesforce Accounts</h1>
              <p className="accounts-page__loading-copy">
                Fetching data from the TriServe Salesforce org and preparing the customer accounts workspace.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="accounts-page" data-testid="accounts-page">
      <PageHeader
        eyebrow="Salesforce Accounts"
        title="Accounts"
        description="Manage and track utility customer accounts from Salesforce using the TriServe Energy portal theme."
        icon="lightning-charge-fill"
        iconTone="primary"
        actions={(
          <>
            {lastUpdated ? (
              <span className="badge text-bg-light border" data-testid="accounts-last-updated">
                Updated {formatDateTime(lastUpdated)}
              </span>
            ) : null}
            <button
              type="button"
              className="btn btn-tse-primary"
              onClick={loadAccounts}
              data-testid="accounts-refresh"
            >
              <i className="bi bi-arrow-repeat me-2" aria-hidden="true"></i>
              Refresh accounts
            </button>
          </>
        )}
      />

      {error ? (
        <InlineAlert
          variant="danger"
          icon="exclamation-octagon"
          title="Accounts unavailable."
          message={` ${error}`}
          className="mb-0"
          dataTestId="accounts-error"
        >
          <div className="mt-3">
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={loadAccounts}>
              Retry
            </button>
          </div>
        </InlineAlert>
      ) : null}

      {exportError ? (
        <InlineAlert
          variant="danger"
          icon="file-earmark-excel"
          title="Export failed."
          message={` ${exportError}`}
          className="mb-0"
          dataTestId="accounts-export-error"
        />
      ) : null}

      <div className="row g-3">
        <div className="col-12 col-md-6 col-xl-3">
          <div className="card shadow-sm accounts-page__metric">
            <div className="card-body">
              <div className="accounts-page__metric-icon accounts-page__metric-icon--primary" aria-hidden="true">
                <i className="bi bi-lightning-charge-fill"></i>
              </div>
              <span className="accounts-page__metric-label">Total Accounts</span>
              <div className="accounts-page__metric-value" data-testid="accounts-metric-total">
                {accounts.length}
              </div>
              <p className="accounts-page__metric-copy">Utility accounts loaded from the TriServe API.</p>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <div className="card shadow-sm accounts-page__metric">
            <div className="card-body">
              <div className="accounts-page__metric-icon accounts-page__metric-icon--success" aria-hidden="true">
                <i className="bi bi-calendar-plus"></i>
              </div>
              <span className="accounts-page__metric-label">New In Last 30 Days</span>
              <div className="accounts-page__metric-value" data-testid="accounts-metric-new-30-days">
                {metrics.newInLast30Days}
              </div>
              <p className="accounts-page__metric-copy">Recently created utility customer accounts in Salesforce.</p>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <div className="card shadow-sm accounts-page__metric">
            <div className="card-body">
              <div className="accounts-page__metric-icon accounts-page__metric-icon--info" aria-hidden="true">
                <i className="bi bi-person-x"></i>
              </div>
              <span className="accounts-page__metric-label">Missing Contact Data</span>
              <div className="accounts-page__metric-value" data-testid="accounts-metric-missing-contact">
                {metrics.missingContactData}
              </div>
              <p className="accounts-page__metric-copy">
                Accounts missing phone or complete billing-address data.
              </p>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <div className="card shadow-sm accounts-page__metric">
            <div className="card-body">
              <div className="accounts-page__metric-icon accounts-page__metric-icon--warning" aria-hidden="true">
                <i className="bi bi-tools"></i>
              </div>
              <span className="accounts-page__metric-label">Recent Service Requests</span>
              <div className="accounts-page__metric-value" data-testid="accounts-metric-service-requests">
                {metrics.recentServiceRequests}
              </div>
              <p className="accounts-page__metric-copy">
                Future feature placeholder for utility service activity.
              </p>
            </div>
          </div>
        </div>
      </div>

      {sortedAccounts.length === 0 && !error ? (
        <EmptyState
          icon="people"
          eyebrow="Accounts"
          title="No accounts found"
          message={searchQuery.trim()
            ? 'Try adjusting your search criteria.'
            : 'The TriServe API responded successfully, but no Salesforce accounts matched Industry = Utilities.'}
          actions={(
            <button type="button" className="btn btn-outline-tse-primary" onClick={loadAccounts}>
              Try again
            </button>
          )}
          className="accounts-page__empty"
          testId="accounts-empty"
        />
      ) : null}

      {accounts.length > 0 ? (
        <div className="card shadow-sm accounts-page__table-card">
          <div className="card-body accounts-page__table-body">
            <div className="d-flex flex-column flex-lg-row gap-3 justify-content-between align-items-lg-center mb-3">
              <div>
                <h2 className="h5 mb-1">My Accounts</h2>
                <p className="text-muted mb-0">Search, review, and paginate through utility customer accounts.</p>
              </div>
              <div className="accounts-page__toolbar">
                <div className="btn-group btn-group-sm accounts-page__toolbar-actions" role="group" aria-label="Account export actions">
                  <button
                    type="button"
                    className="btn btn-outline-tse-primary"
                    onClick={() => handleExport('xlsx')}
                    disabled={sortedAccounts.length === 0 || isExporting}
                    data-testid="accounts-export-excel"
                    aria-label="Export filtered accounts to Excel"
                  >
                    <i className="bi bi-file-earmark-excel me-2" aria-hidden="true"></i>
                    {isExporting ? 'Exporting...' : 'Export Excel'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => handleExport('csv')}
                    disabled={sortedAccounts.length === 0 || isExporting}
                    data-testid="accounts-export-csv"
                    aria-label="Export filtered accounts to CSV"
                  >
                    <i className="bi bi-filetype-csv me-2" aria-hidden="true"></i>
                    CSV
                  </button>
                </div>
                <div className="input-group input-group-sm accounts-page__search">
                  <input
                    type="text"
                    className="form-control"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search accounts..."
                    aria-label="Search accounts"
                    data-testid="accounts-search"
                  />
                  <span className="input-group-text bg-white">
                    <i className="bi bi-search" aria-hidden="true"></i>
                  </span>
                </div>
                <span className="accounts-page__export-status text-muted" data-testid="accounts-export-status" aria-live="polite">
                  {isExporting ? `Exporting ${sortedAccounts.length} filtered accounts` : `Ready to export ${sortedAccounts.length} filtered accounts`}
                </span>
              </div>
            </div>

            <div className="table-responsive accounts-page__table-wrap">
              <table className="table table-hover align-middle accounts-page__table">
                <caption className="visually-hidden">Salesforce utility customer accounts with sortable columns for name, TSE account ID, Salesforce ID, industry, type, phone, location, created date, and account actions.</caption>
                <thead className="table-light">
                  <tr>
                    <th scope="col" className="accounts-page__th accounts-page__th--primary" aria-sort={getAriaSort('name')}>
                      <button type="button" className="accounts-page__sort-button" onClick={() => handleSort('name')}>
                        <span>Name</span>
                        <i className={`bi ${getSortIcon('name')}`} aria-hidden="true"></i>
                      </button>
                    </th>
                    <th scope="col" className="accounts-page__th accounts-page__th--id" aria-sort={getAriaSort('accountNumber')}>
                      <button
                        type="button"
                        className="accounts-page__sort-button"
                        onClick={() => handleSort('accountNumber')}
                        aria-label="Sort by TSE account ID"
                      >
                        <span>TSE Acct ID</span>
                        <i className={`bi ${getSortIcon('accountNumber')}`} aria-hidden="true"></i>
                      </button>
                    </th>
                    <th scope="col" className="accounts-page__th accounts-page__th--id" aria-sort={getAriaSort('id')}>
                      <button
                        type="button"
                        className="accounts-page__sort-button"
                        onClick={() => handleSort('id')}
                        aria-label="Sort by Salesforce ID"
                      >
                        <span>SFDC ID</span>
                        <i className={`bi ${getSortIcon('id')}`} aria-hidden="true"></i>
                      </button>
                    </th>
                    <th scope="col" className="accounts-page__th" aria-sort={getAriaSort('industry')}>
                      <button type="button" className="accounts-page__sort-button" onClick={() => handleSort('industry')}>
                        <span>Industry</span>
                        <i className={`bi ${getSortIcon('industry')}`} aria-hidden="true"></i>
                      </button>
                    </th>
                    <th scope="col" className="accounts-page__th" aria-sort={getAriaSort('type')}>
                      <button type="button" className="accounts-page__sort-button" onClick={() => handleSort('type')}>
                        <span>Type</span>
                        <i className={`bi ${getSortIcon('type')}`} aria-hidden="true"></i>
                      </button>
                    </th>
                    <th scope="col" className="accounts-page__th" aria-sort={getAriaSort('phone')}>
                      <button type="button" className="accounts-page__sort-button" onClick={() => handleSort('phone')}>
                        <span>Phone</span>
                        <i className={`bi ${getSortIcon('phone')}`} aria-hidden="true"></i>
                      </button>
                    </th>
                    <th scope="col" className="accounts-page__th" aria-sort={getAriaSort('location')}>
                      <button type="button" className="accounts-page__sort-button" onClick={() => handleSort('location')}>
                        <span>Location</span>
                        <i className={`bi ${getSortIcon('location')}`} aria-hidden="true"></i>
                      </button>
                    </th>
                    <th scope="col" className="accounts-page__th accounts-page__th--date" aria-sort={getAriaSort('createdDate')}>
                      <button type="button" className="accounts-page__sort-button" onClick={() => handleSort('createdDate')}>
                        <span>Created</span>
                        <i className={`bi ${getSortIcon('createdDate')}`} aria-hidden="true"></i>
                      </button>
                    </th>
                    <th scope="col" className="accounts-page__th accounts-page__th--actions">
                      <span>Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagedAccounts.map((account, index) => {
                    const salesforceUrl = getSalesforceAccountUrl(account);
                    const automationKey = getAccountAutomationKey(account, index);

                    return (
                      <tr
                        key={account?.id || `${account?.name}-${account?.accountNumber}`}
                        className="accounts-page__row"
                        data-testid={`accounts-row-${automationKey}`}
                        aria-label={`Account row for ${account?.name || 'unnamed account'}`}
                      >
                        <td className="accounts-page__cell accounts-page__cell--primary" data-testid={`accounts-cell-name-${automationKey}`}>
                          <div className="accounts-page__account-name">{account?.name || 'Unnamed account'}</div>
                        </td>
                        <td className="accounts-page__cell accounts-page__cell--id" data-testid={`accounts-cell-tse-id-${automationKey}`}>
                          <span className="accounts-page__meta-value accounts-page__meta-value--mono">
                            {account?.accountNumber || 'Unavailable'}
                          </span>
                        </td>
                        <td className="accounts-page__cell accounts-page__cell--id" data-testid={`accounts-cell-sfdc-id-${automationKey}`}>
                          {salesforceUrl ? (
                            <a
                              href={salesforceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="accounts-page__link accounts-page__link--salesforce accounts-page__meta-value accounts-page__meta-value--mono"
                              title="Open account in Salesforce"
                              data-testid={`accounts-link-sfdc-${automationKey}`}
                              aria-label={`Open Salesforce record for ${account?.name || 'account'}`}
                            >
                              <i className="bi bi-lightning-charge-fill me-1" aria-hidden="true"></i>
                              {account?.id || 'Unavailable'}
                            </a>
                          ) : (
                            <span className="accounts-page__meta-value accounts-page__meta-value--mono">{account?.id || 'Unavailable'}</span>
                          )}
                        </td>
                        <td className="accounts-page__cell" data-testid={`accounts-cell-industry-${automationKey}`}>{account?.industry || 'Unavailable'}</td>
                        <td className="accounts-page__cell" data-testid={`accounts-cell-type-${automationKey}`}>
                          <span className={getTypeBadgeClassName(account?.type)}>{account?.type || 'Unspecified'}</span>
                        </td>
                        <td className="accounts-page__cell" data-testid={`accounts-cell-phone-${automationKey}`}>
                          {account?.phone ? (
                            <a
                              href={`tel:${account.phone}`}
                              className="accounts-page__link accounts-page__muted-link"
                              data-testid={`accounts-link-phone-${automationKey}`}
                              aria-label={`Call ${account?.name || 'account'}`}
                            >
                              <i className="bi bi-telephone me-1" aria-hidden="true"></i>
                              {account.phone}
                            </a>
                          ) : 'Unavailable'}
                        </td>
                        <td className="accounts-page__cell" title={formatAddress(account)} data-testid={`accounts-cell-location-${automationKey}`}>{getLocationString(account)}</td>
                        <td
                          className="accounts-page__cell accounts-page__cell--date"
                          title={account?.createdDate ? new Date(account.createdDate).toLocaleString('en-US') : 'Unavailable'}
                          data-testid={`accounts-cell-created-${automationKey}`}
                        >
                          {formatRelativeTime(account?.createdDate)}
                        </td>
                        <td className="accounts-page__cell accounts-page__cell--actions" data-testid={`accounts-cell-actions-${automationKey}`}>
                          <div className="d-flex gap-2 justify-content-end flex-wrap">
                            {salesforceUrl ? (
                              <a
                                href={salesforceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-tse-primary accounts-page__action-button"
                                title="Open account in Salesforce"
                                data-testid={`accounts-action-open-sfdc-${automationKey}`}
                                aria-label={`Open Salesforce record for ${account?.name || 'account'}`}
                              >
                                <i className="bi bi-lightning-charge-fill me-1" aria-hidden="true"></i>
                                Salesforce
                              </a>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary accounts-page__action-button"
                                title="Salesforce link unavailable"
                                data-testid={`accounts-action-open-sfdc-${automationKey}`}
                                aria-label="Salesforce record link unavailable"
                                disabled
                              >
                                <i className="bi bi-lightning-charge-fill me-1" aria-hidden="true"></i>
                                Salesforce
                              </button>
                            )}
                          {account?.id ? (
                            <Link
                              to={`/accounts/${account.id}`}
                              className="btn btn-sm btn-outline-tse-primary accounts-page__action-button"
                              data-testid={`accounts-details-link-${automationKey}`}
                              aria-label={`View details for ${account?.name || 'account'}`}
                            >
                              Details
                            </Link>
                          ) : (
                            <button type="button" className="btn btn-sm btn-outline-secondary" disabled>
                              Details
                            </button>
                          )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="accounts-page__pagination-row">
              <p className="text-muted mb-0" data-testid="accounts-page-info">{pageInfo}</p>
              <div className="accounts-page__pagination-controls">
                <div className="accounts-page__page-size">
                  <label htmlFor="accountsPageSize" className="form-label mb-0">Show:</label>
                  <select
                    id="accountsPageSize"
                    className="form-select form-select-sm"
                    value={pageSize}
                    onChange={(event) => setPageSize(Number(event.target.value))}
                    aria-label="Accounts page size"
                  >
                    {PAGE_SIZE_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <nav aria-label="Accounts pagination">
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>
                    {pageNumbers.map((pageNumber) => (
                      pageNumber === 'ellipsis-start' || pageNumber === 'ellipsis-end' ? (
                        <li key={pageNumber} className="page-item disabled"><span className="page-link">...</span></li>
                      ) : (
                        <li key={pageNumber} className={`page-item ${pageNumber === currentPage ? 'active' : ''}`}>
                          <button
                            type="button"
                            className="page-link"
                            onClick={() => setCurrentPage(pageNumber)}
                            aria-current={pageNumber === currentPage ? 'page' : undefined}
                          >
                            {pageNumber}
                          </button>
                        </li>
                      )
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default Accounts;